import type { DebugSettings, GeoFix, PositionProvider, Store } from '../contracts';
import { gaussian, metresPerDegree, wrap360 } from '../pose/math';
import { debugStore } from '../settings';
import { Listeners } from './listeners';

export type PositionErrorCode = 'denied' | 'unavailable' | 'timeout';
export interface PositionError {
  code: PositionErrorCode;
  message: string;
}

/** Rejection reason of GeolocationProvider.start(). */
export class PositionProviderError extends Error {
  readonly code: PositionErrorCode;
  constructor(err: PositionError) {
    super(err.message);
    this.name = 'PositionProviderError';
    this.code = err.code;
  }
}

/** GeolocationPositionError.code -> contract error code. */
export function mapGeolocationError(err: { code: number; message?: string }): PositionError {
  const message = err.message ?? '';
  switch (err.code) {
    case 1: // PERMISSION_DENIED
      return { code: 'denied', message: message || 'Standortzugriff verweigert' };
    case 3: // TIMEOUT
      return { code: 'timeout', message: message || 'Zeitüberschreitung beim Standort' };
    default: // POSITION_UNAVAILABLE and anything else
      return { code: 'unavailable', message: message || 'Standort nicht verfügbar' };
  }
}

/** Max plausible age (ms) of position.timestamp; beyond that the arrival time is used. */
const MAX_TIMESTAMP_AGE_MS = 1500;

// ─── Last-known position cache (display only) ─────────────────────────────────
//
// Stores the last good real-GPS fix in localStorage so the minimap can centre on something
// immediately at startup, before a live fix arrives. Strictly display-only: never read by the
// Kalman filter, the GPS gate, zone queries, distances, or the AR overlay (only app.ts's map
// wiring reads it, via readLastFixCache()).

export const LASTFIX_STORAGE_KEY = 'uferzone.lastfix.v1';
/** Cache entries older than this are ignored (treated as if absent). */
export const LASTFIX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Throttle: at most one localStorage write per this many ms. */
export const LASTFIX_MIN_WRITE_INTERVAL_MS = 10_000;

export interface LastFixCacheEntry {
  lat: number;
  lon: number;
  accuracy: number;
  /** Date.now()-epoch ms when the fix was received. */
  timestamp: number;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Storage abstraction so this stays testable in Node without a real localStorage. */
export type MinimalStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Read the cached last-known fix, or null if there is none, it's malformed, or it's older than
 * `maxAgeMs` (default 7 days). `now` defaults to Date.now(), injectable for tests.
 */
export function readLastFixCache(
  storage: MinimalStorage | undefined,
  now: number = Date.now(),
  maxAgeMs: number = LASTFIX_MAX_AGE_MS,
): LastFixCacheEntry | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(LASTFIX_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<LastFixCacheEntry>;
    if (!isFiniteNumber(v.lat) || !isFiniteNumber(v.lon) || !isFiniteNumber(v.accuracy) || !isFiniteNumber(v.timestamp)) {
      return null;
    }
    const age = now - v.timestamp;
    if (age < 0 || age > maxAgeMs) return null;
    return { lat: v.lat, lon: v.lon, accuracy: v.accuracy, timestamp: v.timestamp };
  } catch {
    return null;
  }
}

function writeLastFixCache(storage: MinimalStorage | undefined, entry: LastFixCacheEntry): void {
  if (!storage) return;
  try {
    storage.setItem(LASTFIX_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* ignore (no storage / quota) */
  }
}

/** Throttled writer (≤ 1 write / `minIntervalMs`, default 10 s) for the last-known-fix cache. */
export class LastFixCacheWriter {
  private readonly storage: MinimalStorage | undefined;
  private readonly minIntervalMs: number;
  private lastWriteMs = -Infinity;

  constructor(storage?: MinimalStorage, minIntervalMs: number = LASTFIX_MIN_WRITE_INTERVAL_MS) {
    this.storage = storage;
    this.minIntervalMs = minIntervalMs;
  }

  /** Writes `entry` if at least `minIntervalMs` passed since the last write; returns whether it wrote. */
  maybeWrite(entry: LastFixCacheEntry, now: number = Date.now()): boolean {
    if (now - this.lastWriteMs < this.minIntervalMs) return false;
    this.lastWriteMs = now;
    writeLastFixCache(this.storage, entry);
    return true;
  }
}

function finiteOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * GeolocationPosition -> GeoFix. `time` is in the performance.now() timebase:
 * the arrival time minus the fix age from position.timestamp (epoch ms) when
 * that age is plausible (0..1.5 s), else the arrival time.
 */
export function geoFixFromPosition(
  pos: { coords: Pick<GeolocationCoordinates, 'latitude' | 'longitude' | 'accuracy' | 'speed' | 'heading'>; timestamp: number },
  perfNow: number,
  dateNow: number,
): GeoFix {
  const c = pos.coords;
  const age = dateNow - pos.timestamp;
  const time = age >= 0 && age <= MAX_TIMESTAMP_AGE_MS ? perfNow - age : perfNow;
  const speed = finiteOrNull(c.speed);
  let course = finiteOrNull(c.heading);
  if (course !== null && (speed === null || speed <= 0)) course = null;
  return {
    lat: c.latitude,
    lon: c.longitude,
    accuracy: c.accuracy,
    course: course === null ? null : wrap360(course),
    speed: speed === null ? null : Math.max(0, speed),
    time,
    source: 'gps',
  };
}

export interface GeolocationProviderOptions {
  geolocation?: Geolocation;
  now?: () => number;
  dateNow?: () => number;
  positionOptions?: PositionOptions;
  /** Last-known-fix cache storage (display only); defaults to window.localStorage if present. */
  lastFixStorage?: MinimalStorage;
}

/**
 * Geolocation API provider (watchPosition, high accuracy, no caching).
 *
 * start() resolves with the first fix, or with the first non-permission error
 * (timeout/unavailable: the watch keeps running). It rejects with a
 * PositionProviderError (code 'denied') if permission is refused. All errors
 * are also reported via onError().
 */
export class GeolocationProvider implements PositionProvider {
  private readonly geo: Geolocation | undefined;
  private readonly now: () => number;
  private readonly dateNow: () => number;
  private readonly positionOptions: PositionOptions;
  private watchId: number | null = null;
  private last: GeoFix | null = null;
  private readonly fixes = new Listeners<GeoFix>();
  private readonly errors = new Listeners<PositionError>();
  private startPromise: Promise<void> | null = null;
  private settle: { resolve: () => void; reject: (e: unknown) => void } | null = null;
  private readonly lastFixWriter: LastFixCacheWriter;

  constructor(opts: GeolocationProviderOptions = {}) {
    this.geo = opts.geolocation ?? (typeof navigator !== 'undefined' ? navigator.geolocation : undefined);
    this.now = opts.now ?? (() => performance.now());
    this.dateNow = opts.dateNow ?? (() => Date.now());
    this.positionOptions = opts.positionOptions ?? { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 };
    const storage = opts.lastFixStorage ?? (typeof localStorage !== 'undefined' ? localStorage : undefined);
    this.lastFixWriter = new LastFixCacheWriter(storage);
  }

  get running(): boolean {
    return this.watchId !== null;
  }

  start(): Promise<void> {
    if (this.startPromise && this.watchId !== null) return this.startPromise;
    this.startPromise = new Promise<void>((resolve, reject) => {
      if (!this.geo) {
        const err: PositionError = { code: 'unavailable', message: 'Geolocation wird nicht unterstützt' };
        this.errors.emit(err);
        reject(new PositionProviderError(err));
        return;
      }
      this.settle = { resolve, reject };
      this.watchId = this.geo.watchPosition(
        (pos) => this.handlePosition(pos),
        (err) => this.handleError(err),
        this.positionOptions,
      );
    });
    return this.startPromise;
  }

  stop(): void {
    if (this.watchId !== null && this.geo) this.geo.clearWatch(this.watchId);
    this.watchId = null;
    this.startPromise = null;
    this.settle = null;
  }

  latest(): GeoFix | null {
    return this.last;
  }

  onFix(cb: (fix: GeoFix) => void): () => void {
    return this.fixes.add(cb);
  }

  onError(cb: (err: PositionError) => void): () => void {
    return this.errors.add(cb);
  }

  private handlePosition(pos: GeolocationPosition): void {
    const fix = geoFixFromPosition(pos, this.now(), this.dateNow());
    if (!Number.isFinite(fix.lat) || !Number.isFinite(fix.lon)) return;
    this.last = fix;
    // Display-only cache (throttled): never consulted by the Kalman filter, the GPS gate, zone
    // queries, distances, or the AR overlay — only app.ts reads it back, for the minimap.
    const dateNow = this.dateNow();
    this.lastFixWriter.maybeWrite(
      { lat: fix.lat, lon: fix.lon, accuracy: fix.accuracy, timestamp: dateNow },
      dateNow,
    );
    if (this.settle) {
      this.settle.resolve();
      this.settle = null;
    }
    this.fixes.emit(fix);
  }

  private handleError(e: GeolocationPositionError): void {
    const err = mapGeolocationError(e);
    if (err.code === 'denied') {
      // The watch is dead after a permission error.
      if (this.watchId !== null && this.geo) this.geo.clearWatch(this.watchId);
      this.watchId = null;
      this.startPromise = null;
      if (this.settle) this.settle.reject(new PositionProviderError(err));
    } else if (this.settle) {
      this.settle.resolve();
    }
    this.settle = null;
    this.errors.emit(err);
  }
}

// ─── Mock ────────────────────────────────────────────────────────────────────

export interface MockPositionOptions {
  store?: Store<DebugSettings>;
  /** Heading (deg true) the mock moves along; null/undefined -> debug.mockHeadingDeg. */
  getHeadingDeg?: () => number | null;
  /** Add gaussian noise scaled to the accuracy (default true). */
  noise?: boolean;
  random?: () => number;
  now?: () => number;
  intervalMs?: number;
}

/** 68 % radius -> per-axis sigma for a circular 2D gaussian. */
const R68_PER_SIGMA = Math.sqrt(-2 * Math.log(1 - 0.68));

/**
 * Mock GPS driven by the debug store (mockLat/mockLon/mockAccuracyM/
 * mockSpeedKmh). Emits a fix every second; moves at mockSpeedKmh along
 * getHeadingDeg() and writes the moved position back to the store.
 */
export class MockPositionProvider implements PositionProvider {
  private readonly store: Store<DebugSettings>;
  private readonly getHeadingDeg: () => number | null;
  private readonly noise: boolean;
  private readonly random: () => number;
  private readonly now: () => number;
  private readonly intervalMs: number;
  private readonly fixes = new Listeners<GeoFix>();
  private readonly errors = new Listeners<PositionError>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private unsub: (() => void) | null = null;
  private last: GeoFix | null = null;
  /** Noise-free position. */
  private lat: number;
  private lon: number;
  private lastTick = NaN;
  private readonly mpd = { lat: 0, lon: 0 };

  constructor(opts: MockPositionOptions = {}) {
    this.store = opts.store ?? debugStore;
    this.getHeadingDeg = opts.getHeadingDeg ?? (() => null);
    this.noise = opts.noise ?? true;
    this.random = opts.random ?? Math.random;
    this.now = opts.now ?? (() => performance.now());
    this.intervalMs = opts.intervalMs ?? 1000;
    const d = this.store.get();
    this.lat = d.mockLat;
    this.lon = d.mockLon;
  }

  /** Noise-free mock position. */
  get truePosition(): { lat: number; lon: number } {
    return { lat: this.lat, lon: this.lon };
  }

  start(): Promise<void> {
    if (this.timer === null) {
      const d = this.store.get();
      this.lat = d.mockLat;
      this.lon = d.mockLon;
      this.unsub = this.store.subscribe((v) => {
        if (v.mockLat !== this.lat || v.mockLon !== this.lon) {
          // Changed from outside (UI preset / input): jump there.
          this.lat = v.mockLat;
          this.lon = v.mockLon;
        }
      });
      this.lastTick = NaN;
      this.tick();
      this.timer = setInterval(() => this.tick(), this.intervalMs);
    }
    return Promise.resolve();
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.unsub?.();
    this.unsub = null;
  }

  latest(): GeoFix | null {
    return this.last;
  }

  onFix(cb: (fix: GeoFix) => void): () => void {
    return this.fixes.add(cb);
  }

  onError(cb: (err: PositionError) => void): () => void {
    return this.errors.add(cb);
  }

  /** Move the mock position by metres (east, north); updates the debug store and emits a fix. */
  nudge(dEast: number, dNorth: number): void {
    this.move(dEast, dNorth);
    this.emitFix(this.now(), this.speedMs(), this.currentHeading());
  }

  /** Advance the movement to `now` and emit one fix (called by the timer; public for tests). */
  tick(now: number = this.now()): GeoFix {
    const speed = this.speedMs();
    const heading = this.currentHeading();
    if (!Number.isNaN(this.lastTick) && speed > 0) {
      const dt = Math.max(0, now - this.lastTick) / 1000;
      const r = heading * (Math.PI / 180);
      this.move(speed * dt * Math.sin(r), speed * dt * Math.cos(r));
    }
    this.lastTick = now;
    return this.emitFix(now, speed, heading);
  }

  private speedMs(): number {
    const kmh = this.store.get().mockSpeedKmh;
    return Number.isFinite(kmh) && kmh > 0 ? kmh / 3.6 : 0;
  }

  private currentHeading(): number {
    const h = this.getHeadingDeg();
    return wrap360(typeof h === 'number' && Number.isFinite(h) ? h : this.store.get().mockHeadingDeg);
  }

  private move(dEast: number, dNorth: number): void {
    metresPerDegree(this.lat, this.mpd);
    this.lat += dNorth / this.mpd.lat;
    this.lon += dEast / this.mpd.lon;
    this.store.update({ mockLat: this.lat, mockLon: this.lon });
  }

  private emitFix(now: number, speed: number, heading: number): GeoFix {
    const acc = Math.max(0.1, this.store.get().mockAccuracyM);
    let lat = this.lat;
    let lon = this.lon;
    if (this.noise) {
      const sigma = acc / R68_PER_SIGMA;
      metresPerDegree(lat, this.mpd);
      lat += (gaussian(this.random) * sigma) / this.mpd.lat;
      lon += (gaussian(this.random) * sigma) / this.mpd.lon;
    }
    const fix: GeoFix = {
      lat,
      lon,
      accuracy: acc,
      course: speed > 0 ? heading : null,
      speed,
      time: now,
      source: 'mock',
    };
    this.last = fix;
    this.fixes.emit(fix);
    return fix;
  }
}
